import type { EntryView, LedgerDay, LedgerReading } from '@aj/core';
import { Box, Button, ChipGroup, HStack, Pressable, Text, VStack } from '@aj/ui';
import { useState } from 'react';

import { LogForm } from '../components/LogForm.js';
import { useLedger, useLedgerSummary, useLoggedTracks } from '../data/hooks.js';
import { useAppData } from '../data/AppDataProvider.js';
import { dateToMs } from '../lib/dates.js';
import { BOTTOM_CLEARANCE } from '../lib/layout.js';
import { useI18n, useT } from '../providers/I18nProvider.js';

/**
 * খাতা — the ledger (§47).
 *
 * Everything that happened, in order, and the only place a record can be
 * corrected or removed. That second job is not a convenience: an append-only
 * log with no way to fix a mistyped number produces a record that is
 * permanently wrong, and this app's whole claim is that its record is honest.
 *
 * ONE LIST, three row types — a study entry, a recorded number, and the
 * marker for days where nothing happened. Splitting readings onto their own
 * screen would make a student remember which drawer a thing went into, and
 * would break the only promise this screen makes: that it holds everything.
 *
 * No card per day. Fourteen bordered boxes is a great deal of furniture for
 * what is fundamentally a list, and the date heading already separates days.
 * A sticky header keeps the reader's place instead.
 */

/** Days shown at once, and how many more each "Show earlier" adds. */
const PAGE = 14;

/** Window the summary strip totals. A month is what people think in. */
const SUMMARY_DAYS = 30;

/** Longest session drawn as a full magnitude bar. Above this the bar is full
 *  and the numeral carries the rest — a three-hour sitting should not squash
 *  every ordinary hour into a stub. */
const BAR_CEILING = 120;

/** Marks a reading row. A glyph rather than a hue, so the row reads as a
 *  different kind of thing without anyone having to learn a legend. */
const READING_GLYPH = '◆';

/** The "no filter" chip's id. Safe as the empty string: a track name is
 *  trimmed user text and can never be empty. */
const ALL_TRACKS = '';

export function Khata() {
  const t = useT();
  const i18n = useI18n();
  const [days, setDays] = useState(PAGE);
  const [track, setTrack] = useState<string | undefined>(undefined);
  const [editing, setEditing] = useState<string | null>(null);

  const tracks = useLoggedTracks();
  const ledger = useLedger(days, track);
  const summary = useLedgerSummary(SUMMARY_DAYS);

  // Asking for one more than we show is how "is there more?" gets answered
  // without a second pass over the log.
  const more = useLedger(days + 1, track).length > ledger.length;

  return (
    <Box as="main" className={`min-h-0 flex-1 overflow-y-auto ${BOTTOM_CLEARANCE}`}>
      <VStack gap={4} className="mx-auto w-full max-w-xl px-5 pt-5">
        <VStack gap={1}>
          <Text as="h1" variant="title" tone="ink">
            {t('khata.title')}
          </Text>
          {/* The summary answers "how has the month gone" before the reader
              has to add anything up themselves. */}
          {summary.entries > 0 && (
            <Text variant="caption" tone="muted" numeric>
              {t('khata.summary', {
                duration: i18n.formatDuration(summary.minutes),
                count: summary.entries,
                track: summary.topTrack ?? '',
              })}
            </Text>
          )}
        </VStack>

        {/* A filter for one subject is worth a row of chips; a filter for one
            subject when there is only one subject is furniture. */}
        {tracks.length > 1 && (
          <ChipGroup
            label={t('khata.allTracks')}
            options={[
              { value: ALL_TRACKS, label: t('khata.allTracks') },
              ...tracks.map((name) => ({ value: name, label: name })),
            ]}
            value={track ?? ALL_TRACKS}
            onSelect={(value) => setTrack(value === ALL_TRACKS ? undefined : value)}
          />
        )}

        {ledger.length === 0 ? (
          <VStack gap={2}>
            <Text variant="label" tone="ink">
              {t('khata.empty.title')}
            </Text>
            <Text variant="body" tone="muted">
              {t('khata.empty.body')}
            </Text>
          </VStack>
        ) : (
          <VStack gap={0}>
            {ledger.map((day) => (
              <Day
                key={day.date}
                day={day}
                editing={editing}
                onEdit={setEditing}
                onDone={() => setEditing(null)}
              />
            ))}

            {more && (
              <Button
                variant="secondary"
                className="mt-4 self-start"
                onClick={() => setDays((n) => n + PAGE)}
              >
                {t('khata.showEarlier')}
              </Button>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
}

type DayProps = {
  day: LedgerDay;
  editing: string | null;
  onEdit: (entryId: string) => void;
  onDone: () => void;
};

function Day({ day, editing, onEdit, onDone }: DayProps) {
  const t = useT();
  const i18n = useI18n();
  const { today } = useAppData();
  const label = dayLabel(day.date, today, i18n, t);

  return (
    <>
      {/* Quiet days as one marker, never as blank rows. It restores the sense
          of elapsed time that skipping empty days costs, without turning the
          screen into a list of things the student failed to do. */}
      {day.gapBefore > 0 && (
        <Text variant="caption" tone="subtle" className="py-3 text-center">
          {t('khata.quietDays', { count: day.gapBefore })}
        </Text>
      )}

      <Box className="border-t border-line pt-3 pb-1">
        {/* Sticky, so a long month never leaves the reader wondering which day
            they are looking at. */}
        <HStack
          gap={3}
          align="baseline"
          justify="between"
          className="sticky top-0 z-10 bg-canvas py-1"
        >
          <Text variant="label" tone="ink">
            {label}
          </Text>
          {day.log.minutes > 0 && (
            <Text variant="caption" tone="muted" numeric>
              {i18n.formatDuration(day.log.minutes)}
            </Text>
          )}
        </HStack>

        <VStack as="ul" gap={0} aria-label={label}>
          {day.log.entries.map((entry) =>
            editing === entry.id ? (
              <Box as="li" key={entry.id} className="py-2">
                <LogForm entry={entry} onDone={onDone} />
              </Box>
            ) : (
              <Box as="li" key={entry.id}>
                <Row entry={entry} onEdit={() => onEdit(entry.id)} />
              </Box>
            ),
          )}
          {day.readings.map((reading) => (
            <Box as="li" key={reading.id}>
              <ReadingRow reading={reading} />
            </Box>
          ))}
        </VStack>
      </Box>
    </>
  );
}

function Row({ entry, onEdit }: { entry: EntryView; onEdit: () => void }) {
  const t = useT();
  const i18n = useI18n();
  // Proportional, capped. Seeing which sittings were long is most of what
  // scanning a ledger is for, and five numerals in a column do not show it.
  const width = Math.max(6, Math.min(100, (entry.minutes / BAR_CEILING) * 100));

  return (
    <Pressable
      onClick={onEdit}
      aria-label={t('log.edit', {
        subject: entry.track,
        minutes: i18n.formatDuration(entry.minutes),
      })}
      className="flex min-h-11 w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-sunken"
    >
      <VStack gap={0} className="min-w-0 flex-1">
        <Text variant="body" tone="ink" className="min-w-0 truncate">
          {entry.intention === undefined
            ? entry.track
            : t('khata.trackTopic', { track: entry.track, topic: entry.intention })}
        </Text>
        {/* Both stated in words. Neither is ever carried by colour alone. */}
        {(entry.kind === 'review' || entry.timed) && (
          <HStack gap={2}>
            {entry.kind === 'review' && (
              <Text variant="caption" tone="subtle">
                {t('log.review')}
              </Text>
            )}
            {entry.timed && (
              <Text variant="caption" tone="subtle">
                {t('log.timed')}
              </Text>
            )}
          </HStack>
        )}
      </VStack>

      <Box className="h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-sunken">
        <Box className="h-full rounded-full bg-line-strong" style={{ width: `${width}%` }} />
      </Box>
      <Text variant="caption" tone="muted" numeric className="w-12 shrink-0 text-right">
        {i18n.formatDuration(entry.minutes)}
      </Text>
    </Pressable>
  );
}

/**
 * A recorded number, in the day it was recorded.
 *
 * Marked with a different glyph rather than a different colour, so it reads
 * as a distinct kind of row without asking anyone to remember a legend.
 */
function ReadingRow({ reading }: { reading: LedgerReading }) {
  const t = useT();
  const i18n = useI18n();

  return (
    <Box className="flex min-h-11 w-full items-center gap-3 px-2 py-1.5">
      <Text variant="caption" tone="subtle" className="shrink-0">
        {READING_GLYPH}
      </Text>
      <Text variant="body" tone="ink" className="min-w-0 flex-1 truncate">
        {reading.measure}
      </Text>
      {reading.delta !== null && reading.delta !== 0 && (
        <Text variant="caption" tone={reading.delta > 0 ? 'accent' : 'subtle'} numeric>
          {t(reading.delta > 0 ? 'khata.up' : 'khata.down', {
            value: Math.abs(reading.delta),
          })}
        </Text>
      )}
      <Text variant="caption" tone="muted" numeric className="shrink-0">
        {reading.outOf === undefined
          ? `${i18n.formatNumber(reading.value)}${reading.unit === undefined ? '' : ` ${reading.unit}`}`
          : t('khata.outOf', { value: reading.value, total: reading.outOf })}
      </Text>
    </Box>
  );
}

/**
 * "Today" and "Yesterday" by name, everything older by date.
 *
 * Relative naming stops at two days on purpose: "5 days ago" makes the reader
 * do arithmetic to place it, and a ledger is for locating a specific day.
 */
function dayLabel(
  date: string,
  today: string,
  i18n: ReturnType<typeof useI18n>,
  t: ReturnType<typeof useT>,
): string {
  if (date === today) return t('khata.today');

  const ms = dateToMs(date);
  if (dateToMs(today) - ms === 86_400_000) return t('khata.yesterday');

  return i18n.formatDate(ms, { weekday: 'short', day: 'numeric', month: 'short' });
}
